import { KeyboardEvent, MouseEvent, PointerEvent, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

import { CitationSource, CitationSourceKind, compactCitationDomain, hostnameFromUrl, isHoverlessDevice } from './citationSource'
import { CITATION_VERIFICATION } from './citationVerification'
import { SourceIcon } from './SourceIcon'
import { SourceHoverCard } from './SourceHoverCard'

export interface SourcePillProps {
  sources: Array<CitationSource>
  testId: string
  /** Defaults to `{testId}-card`. */
  cardTestId?: string
  /** Sits inside prose: tighter spacing, baseline-aligned. */
  inline?: boolean
}

const PILL_ICON_CLASS = 'h-3 w-3 shrink-0 text-muted-foreground'

const KIND_LABEL_KEYS = {
  web: 'threads.detail.sourcePill.kind.web',
  library: 'threads.detail.sourcePill.kind.library',
  social: 'threads.detail.sourcePill.kind.social',
  study: 'threads.detail.sourcePill.kind.study',
  room: 'threads.detail.sourcePill.kind.room',
  run: 'threads.detail.sourcePill.kind.run',
  audience: 'threads.detail.sourcePill.kind.audience',
  dataset: 'threads.detail.sourcePill.kind.dataset',
  connector_record: 'threads.detail.sourcePill.kind.connector_record',
} as const satisfies Record<CitationSourceKind, string>

/** The pill, expanded card and source drawer share one icon owner. */
function PillTypeIcon({ source }: { source: CitationSource }) {
  return <SourceIcon source={{ ...source, hostname: source.hostname ? compactCitationDomain(source.hostname) : hostnameFromUrl(source.href ?? '') }} className={PILL_ICON_CLASS} />
}

const OPEN_DELAY_MS = 150
const CLOSE_GRACE_MS = 120

/**
 * White in light mode, with the existing muted surface in dark mode.
 * The focus ring is a foreground alpha, never the brand accent.
 */
const PILL_CLASS =
  'inline-flex h-5 max-w-full items-center gap-1 rounded-full bg-background dark:bg-muted px-1.5 text-[11px] font-medium leading-none text-muted-foreground ' +
  'hover:bg-background dark:hover:bg-muted-foreground/15 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30'

/**
 * One citation point: the source icon, its short name and `+N` when several
 * sources back the same point. Hover, focus or tap opens the card; click (on
 * a hover device) opens the source itself. On touch the first tap only opens
 * the card, whose "Open source" button opens the source.
 *
 * The pill is a plain button under `PopoverAnchor asChild`, never a
 * `PopoverTrigger` (which toggles on click), and never a `<div>`: inline
 * pills sit inside markdown paragraphs.
 */
export function SourcePill(props: SourcePillProps) {
  const { sources, testId, cardTestId, inline = false } = props

  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)

  const { t } = useTranslation('app')
  const openTimer = useRef<number | null>(null)
  const closeTimer = useRef<number | null>(null)
  const pillRef = useRef<HTMLButtonElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  // A focus that comes from a pointer press is a click, not keyboard focus:
  // the click opens the source, so the card must not open under it.
  const isPointerFocus = useRef(false)
  const cardId = useId()
  useEffect(() => () => {
    if (openTimer.current != null) {
      window.clearTimeout(openTimer.current)
    }
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current)
    }
  }, [])

  const clearTimers = () => {
    if (openTimer.current != null) {
      window.clearTimeout(openTimer.current)
      openTimer.current = null
    }
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  const close = () => {
    clearTimers()
    setOpen(false)
  }
  const onPointerEnter = (event: PointerEvent) => {
    if (event.pointerType === 'touch' || isHoverlessDevice()) {
      return
    }
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
    if (open || openTimer.current != null) {
      return
    }
    openTimer.current = window.setTimeout(() => {
      openTimer.current = null
      setOpen(true)
    }, OPEN_DELAY_MS)
  }
  const onPointerLeave = (event: PointerEvent) => {
    if (event.pointerType === 'touch') {
      return
    }
    if (openTimer.current != null) {
      window.clearTimeout(openTimer.current)
      openTimer.current = null
    }
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current)
    }
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null
      setOpen(false)
    }, CLOSE_GRACE_MS)
  }
  const isInside = (node: EventTarget | null) => {
    if (!(node instanceof Node)) {
      return false
    }
    return Boolean(pillRef.current?.contains(node) || contentRef.current?.contains(node))
  }
  const onBlur = (event: { relatedTarget: EventTarget | null }) => {
    // A press that never became a click (press, then drag off) must not
    // swallow the next keyboard focus.
    isPointerFocus.current = false
    if (event.relatedTarget && !isInside(event.relatedTarget)) {
      close()
    }
  }
  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    isPointerFocus.current = false
    event.preventDefault()
    event.stopPropagation()
    if (isHoverlessDevice()) {
      clearTimers()
      setOpen(true)
      return
    }
    current?.onOpen()
  }
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') {
      close()
      return
    }
    if (sources.length < 2 || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
      return
    }
    event.preventDefault()
    const step = event.key === 'ArrowRight' ? 1 : -1
    setIndex((value) => (value + step + sources.length) % sources.length)
    setOpen(true)
  }

  const first = sources[0]
  const current = sources[Math.min(index, sources.length - 1)]
  if (!first || !current) {
    return null
  }

  const extra = sources.length - 1
  // WCAG 2.5.3: the accessible name carries the visible pill text, so a
  // voice-control user can say what they see.
  const kind = t(KIND_LABEL_KEYS[first.kind])
  const shortName = first.kind === 'web' ? compactCitationDomain(first.shortName) : first.shortName
  const visibleLabel = first.kind === 'connector_record' && first.location
    ? `${shortName} ${first.location}`
    : shortName
  const ariaLabel = extra > 0
    ? t('threads.detail.sourcePill.ariaMore', { label: visibleLabel, count: extra, kind })
    : t('threads.detail.sourcePill.aria', { label: visibleLabel, kind })
  const more = extra > 0 ? (
    <span className="shrink-0" data-testid={`${testId}-more`}>
      {t('threads.detail.sourcePill.more', { count: extra })}
    </span>
  ) : null
  const resolvedCardTestId = cardTestId ?? `${testId}-card`
  const onOpenSource = isHoverlessDevice() ? () => current.onOpen() : undefined

  return (
    <Popover open={open} onOpenChange={(next) => (next ? setOpen(true) : close())} modal={false}>
      <PopoverAnchor asChild>
        <button
          ref={pillRef}
          type="button"
          data-testid={testId}
          data-verification={first.verification}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={open ? cardId : undefined}
          aria-describedby={open ? cardId : undefined}
          className={cn(
            PILL_CLASS,
            CITATION_VERIFICATION[first.verification].markerClassName,
            inline && 'relative -top-px mx-0.5 align-baseline',
          )}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onPointerDown={() => {
            isPointerFocus.current = true
          }}
          onPointerCancel={() => {
            isPointerFocus.current = false
          }}
          onFocus={() => {
            if (isPointerFocus.current) {
              isPointerFocus.current = false
              return
            }
            clearTimers()
            setOpen(true)
          }}
          onBlur={onBlur}
          onClick={onClick}
          onKeyDown={onKeyDown}
        >
          <PillTypeIcon source={first} />
          <span className="max-w-[14ch] truncate" data-testid={`${testId}-label`}>
            {shortName}
          </span>
          {first.kind === 'connector_record' && first.location ? (
            <span className="max-w-[12ch] truncate text-muted-foreground" data-testid={`${testId}-app`}>
              {first.location}
            </span>
          ) : null}
          {more}
        </button>
      </PopoverAnchor>
      <PopoverContent
        ref={contentRef}
        id={cardId}
        side="top"
        align="start"
        collisionPadding={8}
        // A short window fits the card neither above nor below the pill: it
        // scrolls inside the space left instead of running off screen.
        className="w-72 max-w-[calc(100vw-2rem)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-2.5"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (isInside(event.target)) {
            event.preventDefault()
          }
        }}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onBlur={onBlur}
      >
        <SourceHoverCard
          sources={sources}
          index={Math.min(index, sources.length - 1)}
          onIndexChange={setIndex}
          testId={resolvedCardTestId}
          onOpenSource={onOpenSource}
        />
      </PopoverContent>
    </Popover>
  )
}
